import React, { Fragment } from 'react';

import { inToPx } from '@aline/common';
import clsx from 'clsx';
import { range } from 'ramda';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  horizontalContentArea: {
    backgroundColor: 'white',
    height: '100%',
    left: 0,
    position: 'absolute',
    top: 0,
  },
  horizontalInch: {
    color: theme.appColors.iconsType,
    fontFamily: theme.typography.fontFamily,
    fontSize: 10,
    height: '100%',
    left: 0,
    lineHeight: '16px',
    position: 'absolute',
    textAlign: 'center',
    top: 0,
    width: 16,
  },
  horizontalLargeTick: {
    height: 8,
    top: 4,
  },
  horizontalLeftMarginArea: {
    cursor: 'e-resize',
  },
  horizontalMarginArea: {
    height: '100%',
    left: 0,
    position: 'absolute',
    top: 0,
    zIndex: 1,
  },
  horizontalRightMarginArea: {
    cursor: 'w-resize',
  },
  horizontalSmallTick: {
    height: 4,
    top: 6,
  },
  horizontalTick: {
    backgroundColor: theme.appColors.rule,
    left: 0,
    position: 'absolute',
    width: 1,
  },
  horizontalTicks: {
    height: '100%',
    left: 0,
    position: 'absolute',
    top: 0,
    userSelect: 'none',
  },
  verticalBottomMarginArea: {
    cursor: 'n-resize',
  },
  verticalContentArea: {
    backgroundColor: 'white',
    left: 0,
    position: 'absolute',
    top: 0,
    width: '100%',
  },
  verticalInch: {
    color: theme.appColors.iconsType,
    fontFamily: theme.typography.fontFamily,
    fontSize: 10,
    height: 16,
    left: 0,
    lineHeight: '16px',
    position: 'absolute',
    textAlign: 'center',
    top: 0,
    width: '100%',
  },
  verticalLargeTick: {
    left: 4,
    width: 8,
  },
  verticalMarginArea: {
    left: 0,
    position: 'absolute',
    top: 0,
    width: '100%',
    zIndex: theme.zIndex.appBar,
  },
  verticalSmallTick: {
    left: 6,
    width: 4,
  },
  verticalTick: {
    backgroundColor: theme.appColors.rule,
    height: 1,
    position: 'absolute',
    top: 0,
    userSelect: 'none',
  },
  verticalTicks: {
    left: 0,
    position: 'absolute',
    top: 0,
    width: '100%',
  },
  verticalTopMarginArea: {
    cursor: 's-resize',
  },
}));

function translate(direction: 'horizontal' | 'vertical', px: number): string {
  return `translate${direction === 'horizontal' ? 'X' : 'Y'}(${px}px)`;
}

interface TicksProps {
  contentSize: number;
  direction: 'horizontal' | 'vertical';
  inchTickCount: number;
  marginStart: number;
  onEndDrag(position: number): void;
  onStartDrag(position: number): void;
}

export function Ticks({
  contentSize,
  direction,
  inchTickCount,
  marginStart,
  onEndDrag,
  onStartDrag,
}: TicksProps): JSX.Element {
  const { classes } = useStyles();
  const sizeAttribute = direction === 'horizontal' ? 'width' : 'height';

  return (
    <div
      className={direction === 'horizontal' ? classes.horizontalTicks : classes.verticalTicks}
      style={{
        transform: translate(direction, inToPx(inchTickCount * -1 + marginStart)),
      }}
    >
      <div
        className={clsx(
          direction === 'horizontal' ? classes.horizontalMarginArea : classes.verticalMarginArea,
          direction === 'horizontal'
            ? classes.horizontalLeftMarginArea
            : classes.verticalTopMarginArea
        )}
        data-ruler={`${direction}-start`}
        style={{ [sizeAttribute]: inToPx(inchTickCount) }}
        onMouseDown={(event): void => {
          event.preventDefault();
          onStartDrag(direction === 'horizontal' ? event.clientX : event.clientY);
        }}
      />
      <div
        className={clsx(
          direction === 'horizontal' ? classes.horizontalMarginArea : classes.verticalMarginArea,
          direction === 'horizontal'
            ? classes.horizontalRightMarginArea
            : classes.verticalBottomMarginArea
        )}
        data-ruler={`${direction}-end`}
        style={{
          [sizeAttribute]: inToPx(inchTickCount - contentSize),
          transform: translate(direction, inToPx(inchTickCount + contentSize)),
        }}
        onMouseDown={(event): void => {
          event.preventDefault();
          onEndDrag(direction === 'horizontal' ? event.clientX : event.clientY);
        }}
      />
      <div
        className={
          direction === 'horizontal' ? classes.horizontalContentArea : classes.verticalContentArea
        }
        style={{
          [sizeAttribute]: inToPx(contentSize),
          transform: translate(direction, inToPx(inchTickCount)),
        }}
      />
      {range(0, inchTickCount * 2).map((inch: number) => {
        const number = Math.abs(inch - inchTickCount);
        return (
          <Fragment key={inch}>
            {range(1, 8).map((tick: number) => {
              return (
                <div
                  key={tick}
                  className={clsx(
                    direction === 'horizontal' ? classes.horizontalTick : classes.verticalTick,
                    direction === 'horizontal'
                      ? classes[tick === 4 ? 'horizontalLargeTick' : 'horizontalSmallTick']
                      : classes[tick === 4 ? 'verticalLargeTick' : 'verticalSmallTick']
                  )}
                  style={{ transform: translate(direction, inToPx(inch + tick / 8)) }}
                />
              );
            })}
            <div
              className={direction === 'horizontal' ? classes.horizontalInch : classes.verticalInch}
              style={{ transform: translate(direction, inToPx(inch) - 8) }}
            >
              {number !== 0 ? number : null}
            </div>
          </Fragment>
        );
      })}
    </div>
  );
}
